import { useState } from "react";
import PageHeader from "../../Component/BasicComponents/HeaderBreadCrumbs";
import BasicDataTable from "../../Component/BasicComponents/BasicDataTable";
import RoundedDropdown from "../../Component/BasicComponents/Dropdowns/RoundedDropdown";
import { useNavigate } from "react-router-dom";
import {
  useOrderListData,
  useOrderStatusData,
  useOrderTypes,
} from "./hooks/usePOB";

import { FaEye } from "react-icons/fa";
import CustomDropdown from "../../Component/BasicComponents/Dropdowns/RoundedDropdownPagination";
import { getOutletFiltersData } from "./hooks/POBRepository";
import { useExcelPolling } from "../../hooks/useExcelPolling";
import { COLORS } from "../../constants/colors";
import { showToast } from "../../utils/toastUtils";
import axiosInstance from "../../Api/axiosInstance";
import { API_ENDPOINTS } from "../../Api/apiEndPoints";

const POBOrdersList = () => {
  const navigate = useNavigate();
  const { startPolling, stopPolling } = useExcelPolling();
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [excelStatus, setExcelStatus] = useState<string | null>("Loading..");

  const [filtersState, setFiltersState] = useState<any>({
    page: 1,
    limit: 10,
    search: "",
    sortOrder: "",
    sortColumn: "",
    employeeId: "",
    outletFromId: "",
    outletToId: "",
    orderType: "",
    orderStatus: "",
    startDate: null,
    endDate: null,
  });

  const {
    data: orderList,
    isLoading: orderListLoading,
    refetch,
  } = useOrderListData({
    enable: true,
    params: {
      page: filtersState?.page,
      limit: filtersState?.limit,
      search: filtersState?.search,
      sortOrder: "",
      sortColumn: "",
      employeeId: filtersState?.employeeId,
      outletFromId: filtersState?.outletFromId,
      outletToId: filtersState?.outletToId,
      orderType: filtersState?.orderType,
      orderStatus: filtersState?.orderStatus,
      startDate: null,
      endDate: null,
    },
  });

  const { data: orderTypesData } = useOrderTypes({ enable: true });
  const { data: getOrderStatusData } = useOrderStatusData({ enable: true });

  const filterOrderTypesData = [
    { id: "", label: "All" },
    ...Object.entries(orderTypesData?.data || {}).map((item: any) => ({
      id: item[0],
      label: item[1],
    })),
  ];

  const filterOrderStatusData = [
    { id: "", label: "All" },
    ...Object.entries(getOrderStatusData?.data || {}).map((item: any) => ({
      id: item[0],
      label: item[1],
    })),
  ];

  const handlePagination = (pageNo: number) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        page: pageNo,
      };
    });
  };

  const handleonChangeRowsPerPage = (limit: any, pageNo: any) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        page: pageNo,
        limit: limit,
      };
    });
  };

  const handleSearch = (searchValue: any) => {
    setFiltersState((prevState: any) => {
      return {
        ...prevState,
        search: searchValue,
      };
    });
  };

  const handlePobView = (row: any) => {
    navigate("/pobOrderView", { state: { OrderId: row?.OrderId } });
  };

  const handleFilter = (key: any, selected: any) => {
    setFiltersState((prev: any) => ({
      ...prev,
      [key]:
        selected.id === "null"
          ? null
          : key === "employeeid"
          ? Number(selected.id)
          : selected.id, // Keep as string for other keys like 'orderStatus'
    }));
  };

  const downloadExcelGet = async () => {
    const param: Record<string, any> = {
      ...(filtersState?.search == "" ? {} : { search: filtersState?.search }),
      ...(filtersState?.outletFromId == ""
        ? {}
        : { outletFromId: filtersState?.outletFromId }),
      ...(filtersState?.outletToId == ""
        ? {}
        : { outletToId: filtersState?.outletToId }),
      ...(filtersState?.orderType == ""
        ? {}
        : { orderType: filtersState?.orderType }),
      ...(filtersState?.orderStatus == ""
        ? {}
        : { orderStatus: filtersState?.orderStatus }),
      ...(filtersState.startDate == null
        ? {}
        : { startDate: filtersState.startDate }),
      ...(filtersState.endDate == null
        ? {}
        : { endDate: filtersState.endDate }),
    };

    const queryString = new URLSearchParams(param).toString();
    try {
      setLoadingExcel(true);
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.POB.GET_EXCEL_FOR_ORDER_LIST}?${queryString}`
      );
      const { success, fileDownloadId, intervalTime } = response?.data?.data || null;
      
      if (success && fileDownloadId) {
        startPolling(fileDownloadId, intervalTime, {
          onStatusUpdate: (currentStatus: string) => {
            setExcelStatus(currentStatus);
          },
          onSuccess: (url: any) => {
            setLoadingExcel(false);
            setExcelStatus("Loading..");
            const a = document.createElement("a");
            a.href = url;
            a.download = "report.xlsx";
            a.click();
            showToast.success("The file is ready!");
          },
          onFailed: (msg: any) => {
            showToast.error(msg || "");
            stopPolling();
            setLoadingExcel(false);
          },
          onExpired: (msg: any) => {
            showToast.error(msg || "");
            stopPolling();
            setLoadingExcel(false);
          },
          onDeleted: (msg: any) => {
            showToast.error(msg || "");
            stopPolling();
            setLoadingExcel(false);
          },
        });
      }
    } catch (err: any) {
      showToast.error(err);
    }
  };

  return (
    <div className="p-2">
      <PageHeader
        title="POB"
        buttons={[
          {
            label: loadingExcel
              ? excelStatus
                ? excelStatus.charAt(0).toUpperCase() +
                  excelStatus.slice(1) +
                  ".."
                : ""
              : "Excel",
            onClick: () => downloadExcelGet(),
            color: loadingExcel ? COLORS.disable : COLORS.primary,
            disable: loadingExcel ? true : false,
          },
        ]}
      />
      <BasicDataTable
        paginationTotalRows={orderList?.data?.Pagination?.TotalRecords || 0}
        showSpinner={orderListLoading}
        onChangePage={(pageNo: number) => {
          handlePagination(pageNo);
        }}
        onChangeRowsPerPage={(newPerPage: any, page: any) => {
          handleonChangeRowsPerPage(newPerPage, page);
        }}
        handleSearch={(val: any) => {
          handleSearch(val);
        }}
        Title="POB Orders List"
        columns={PobListColumns(handlePobView)}
        data={orderList?.data?.primaryOrders || []}
        headerContent={
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div style={{ marginLeft: 10 }}>
              <CustomDropdown
                placeholder="All"
                DropdownLabel="Order From"
                value={filtersState?.outletFromId}
                onChange={(id) => {
                  setFiltersState((prev: any) => ({
                    ...prev,
                    outletFromId: id,
                    page: 1,
                  }));
                }}
                fetchData={async ({ page, limit, search }) => {
                  const res = await getOutletFiltersData({
                    page,
                    limit,
                    search,
                  });

                  const rawData = res?.data || {};
                  let formatted = Object.entries(rawData).map(
                    ([id, label]) => ({
                      id,
                      label: String(label),
                    })
                  );

                  // ✅ Add "All" only on the first page
                  if (page === 1) {
                    formatted = [{ id: "", label: "All" }, ...formatted];
                  }
                  // ✅ Remove any duplicate "All" (safety net)
                  formatted = formatted.filter(
                    (item, index, self) =>
                      index ===
                      self.findIndex(
                        (t) =>
                          t.id === item.id &&
                          t.label.toLowerCase() === item.label.toLowerCase()
                      )
                  );

                  const hasMore = Object.keys(rawData).length === limit;

                  return {
                    data: formatted,
                    hasMore,
                  };
                }}
                includeAllOption
              />
            </div>

            <div style={{ marginLeft: 10 }}>
              <CustomDropdown
                placeholder="All"
                DropdownLabel="Order To"
                value={filtersState?.outletToId}
                onChange={(id) => {
                  setFiltersState((prev: any) => ({
                    ...prev,
                    outletToId: id,
                    page: 1,
                  }));
                }}
                fetchData={async ({ page, limit, search }) => {
                  const res = await getOutletFiltersData({
                    page,
                    limit,
                    search,
                  });

                  const rawData = res?.data || {};
                  let formatted = Object.entries(rawData).map(
                    ([id, label]) => ({
                      id,
                      label: String(label),
                    })
                  );

                  // ✅ Add "All" only on the first page
                  if (page === 1) {
                    formatted = [{ id: "", label: "All" }, ...formatted];
                  }

                  // ✅ Remove any duplicate "All" (safety net)
                  formatted = formatted.filter(
                    (item, index, self) =>
                      index ===
                      self.findIndex(
                        (t) =>
                          t.id === item.id &&
                          t.label.toLowerCase() === item.label.toLowerCase()
                      )
                  );

                  const hasMore = Object.keys(rawData).length === limit;

                  return {
                    data: formatted,
                    hasMore,
                  };
                }}
                includeAllOption
              />
            </div>

            <div style={{ marginLeft: 10 }}>
              <RoundedDropdown
                options={filterOrderStatusData || []}
                onSelect={(e) => {
                  handleFilter("orderStatus", e);
                }}
                defaultLabel="All"
                width="auto"
                isShowHeading
                DropdownHeading="Order Status"
                headingColor="#2e8dd3"
              />
            </div>
            <div style={{ marginLeft: 10 }}>
              <RoundedDropdown
                options={filterOrderTypesData || []}
                onSelect={(e) => {
                  handleFilter("orderType", e);
                }}
                defaultLabel="All"
                width="auto"
                isShowHeading
                DropdownHeading="Order Type"
                headingColor="#2e8dd3"
              />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default POBOrdersList;

const PobListColumns = (
  handlePobView: (row: any) => void
  // handleSorting: (column: any, direction: any) => void
) => [
  {
    name: "Order No.",
    selector: (row: { OrderNumber: number | string }) => row.OrderNumber || "-",
    sortable: true,
    sortField: "order_no",
    minWidth: "170px",
    wrap: true,
  },
  {
    name: "Emp Name",
    selector: (row: { EmployeeName: string }) => row?.EmployeeName || "-", // (depend by backend)
    sortable: true,
    sortField: "order_employee",
    wrap: true,
    minWidth: "170px",
  },
  {
    name: "Emp Code",
    selector: (row: { EmployeeCode: string }) => row?.EmployeeCode || "-", // (depend by backend)
    sortable: true,
    sortField: "order_employeeCode",
    wrap: true,
    minWidth: "170px",
  },
  {
    name: "Order Type",
    selector: (row: { OrderType: string }) => row.OrderType || "-",
    sortable: true,
    sortField: "order_type",
    minWidth: "170px",
    wrap: true,
  },
  {
    name: "Order From",
    selector: (row: { OutletFromName: string }) => row?.OutletFromName || "-",
    sortable: true,
    sortField: "outlet_from",
    wrap: true,
    minWidth: "170px",
  },
  {
    name: "Order To",
    selector: (row: { OutletToName: string }) => row?.OutletToName || "-", // (depend by backend)
    sortable: true,
    sortField: "order_to",
    wrap: true,
    minWidth: "170px",
  },
  {
    name: "Order Date",
    selector: (row: { OrderDate: string }) => row?.OrderDate || "-", // (depend by backend)
    sortable: true,
    sortField: "order_date",
    minWidth: "170px",
    wrap: true,
  },
  {
    name: "Actual Order Date",
    selector: (row: { ActualDate: string }) => row?.ActualDate || "-", // (depend by backend)
    sortable: true,
    sortField: "order_date",
    minWidth: "200px",
    wrap: true,
  },
  {
    name: "Order Quantity",
    selector: (row: { OrderQty: string }) =>
      row?.OrderQty ? parseInt(row.OrderQty) : "-", // 👈 removes decimal
    sortable: true,
    sortField: "OrderQty",
    minWidth: "170px",
    wrap: true,
  },
  {
    name: "Order Total",
    selector: (row: { OrderTotal: string }) => row?.OrderTotal || "-", // (depend by backend)
    sortable: true,
    sortField: "order_total",
    minWidth: "170px",
    wrap: true,
    sortFunction: (a: any, b: any) => {
      return Number(a.OrderTotal) - Number(b.OrderTotal);
    },
  },
  {
    name: "Order Status",
    selector: (row: { OrderStatus: string }) => row?.OrderStatus || "-", // (depend by backend)
    sortable: true,
    sortField: "order_status",
    minWidth: "170px",
    wrap: true,
  },

  {
    name: "Action",
    cell: (row: any) => (
      <button
        onClick={() => handlePobView(row)}
        className="btn btn-primary"
        style={{ borderRadius: "20px", padding: "5px", width: "80px" }}
      >
        <FaEye style={{ fill: "#fff" }} /> View
      </button>
    ),
  },
];
